import { EmailAutomationWorkerHealth, EmailAutomationWorkerStatus } from '../types/EmailAutomationWorkerHealth';

export type AutomationBadgeSeverity = 'healthy' | 'warning' | 'error';

export type AutomationBadgeSummary = {
  severity: AutomationBadgeSeverity;
  colorPalette: 'green' | 'yellow' | 'red';
  label: string;
  unhealthyCount: number;
  warningCount: number;
  totalCount: number;
};

const errorStatuses: EmailAutomationWorkerStatus[] = ['Failing', 'TimedOut', 'Stale'];
const warningStatuses: EmailAutomationWorkerStatus[] = ['Disabled', 'RunningLong', 'Unknown'];

export const getAutomationBadgeSummary = (
  workers: EmailAutomationWorkerHealth[] | undefined,
  isError = false
): AutomationBadgeSummary => {
  if (isError) {
    return {
      severity: 'error',
      colorPalette: 'red',
      label: 'Automation',
      unhealthyCount: 1,
      warningCount: 0,
      totalCount: workers?.length ?? 0
    };
  }

  if (!workers || workers.length === 0) {
    return {
      severity: 'warning',
      colorPalette: 'yellow',
      label: 'Automation',
      unhealthyCount: 0,
      warningCount: 1,
      totalCount: 0
    };
  }

  const unhealthyCount = workers.filter((worker) => errorStatuses.includes(worker.Status)).length;
  const warningCount = workers.filter((worker) => warningStatuses.includes(worker.Status)).length;

  if (unhealthyCount > 0) {
    return {
      severity: 'error',
      colorPalette: 'red',
      label: `${unhealthyCount} failing`,
      unhealthyCount,
      warningCount,
      totalCount: workers.length
    };
  }

  if (warningCount > 0) {
    return {
      severity: 'warning',
      colorPalette: 'yellow',
      label: `${warningCount} ${warningCount === 1 ? 'warning' : 'warnings'}`,
      unhealthyCount,
      warningCount,
      totalCount: workers.length
    };
  }

  return {
    severity: 'healthy',
    colorPalette: 'green',
    label: 'Healthy',
    unhealthyCount,
    warningCount,
    totalCount: workers.length
  };
};

export const getWorkerStatusColorPalette = (status: EmailAutomationWorkerStatus): 'green' | 'yellow' | 'red' => {
  if (errorStatuses.includes(status)) {
    return 'red';
  }

  if (warningStatuses.includes(status)) {
    return 'yellow';
  }

  return 'green';
};
